import React, { useState } from 'react';
import { applyAsMentor } from '../services/mentorService';
import { useAlertContext } from '../context/AlertContext';
import { FaUserTie, FaLinkedin, FaBriefcase, FaGraduationCap, FaCamera, FaEnvelope, FaPhone } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const BecomeMentor = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlertContext();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    expertise: '',
    experience: '',
    linkedin: '',
    bio: ''
  });
  const [image, setImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        data.append(key, formData[key]);
      });
      if (image) {
        data.append('image', image);
      }

      const response = await applyAsMentor(data);
      
      if (response.success) {
        showAlert({
          type: 'success',
          title: 'Application Submitted',
          message: 'Your application has been received! We will review it and get back to you shortly.'
        });
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      showAlert({
        type: 'error',
        title: 'Submission Failed',
        message: error.response?.data?.message || 'Something went wrong. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6]" style={{
        backgroundImage: `
          radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
          radial-gradient(at 100% 0%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
          radial-gradient(at 100% 100%, rgba(236, 72, 153, 0.15) 0px, transparent 50%),
          radial-gradient(at 0% 100%, rgba(34, 197, 94, 0.15) 0px, transparent 50%)
        `
      }}>
      {/* Top Decoration */}
      <div className="h-[40rem] bg-[#0F172A] w-full absolute top-0 z-0 rounded-b-[3rem] shadow-2xl overflow-hidden">
             <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
             <div className="absolute top-20 left-20 w-80 h-80 bg-purple-500 rounded-full mix-blend-overlay filter blur-3xl opacity-20 animate-blob"></div>
             <div className="absolute bottom-20 right-20 w-80 h-80 bg-blue-500 rounded-full mix-blend-overlay filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="container-responsive relative z-10 pt-20 px-4 sm:px-6 lg:px-8 pb-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-md border border-white/30 text-white rounded-full text-sm font-medium mb-6 shadow-lg">
            <FaUserTie className="w-4 h-4 mr-2" />
            <span className="tracking-wide uppercase">Join the Elite</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 drop-shadow-lg">
            Become a <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-pink-200">Mentor</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed font-light">
            Share your expertise, guide the next generation, and grow your professional influence.
          </p>
        </div>

        <div className="max-w-4xl mx-auto bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 overflow-hidden relative">
           <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>
           
           <div className="p-8 sm:p-12">
            <form className="space-y-8" onSubmit={handleSubmit}>
                
                {/* Profile Image Section */}
                <div className="flex flex-col items-center justify-center mb-8">
                    <div className="relative group cursor-pointer">
                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-indigo-50 flex items-center justify-center">
                            {previewImage ? (
                                <img src={previewImage} alt="Preview" className="w-full h-full object-cover" />
                            ) : (
                                <FaUserTie className="w-12 h-12 text-indigo-300" />
                            )}
                        </div>
                        <label htmlFor="image" className="absolute inset-0 bg-black/40 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-full cursor-pointer">
                            <FaCamera className="w-8 h-8" />
                        </label>
                        <input
                            type="file"
                            name="image"
                            id="image"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="hidden"
                        />
                    </div>
                    <p className="mt-3 text-sm text-gray-500 font-medium">Upload Profile Photo</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div className="space-y-6">
                        <div>
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 block ml-1">Full Name</label>
                            <div className="relative">
                                <FaUserTie className="absolute top-4 left-4 text-gray-400" />
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    placeholder="e.g. Jane Doe"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 block ml-1">Email Address</label>
                            <div className="relative">
                                <FaEnvelope className="absolute top-4 left-4 text-gray-400" />
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    placeholder="you@example.com"
                                />
                            </div>
                        </div>

                         <div>
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 block ml-1">Years of Experience</label>
                            <div className="relative">
                                <FaBriefcase className="absolute top-4 left-4 text-gray-400" />
                                <input
                                    type="text"
                                    name="experience"
                                    required
                                    value={formData.experience}
                                    onChange={handleChange}
                                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    placeholder="e.g. 5+ Years"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                        <div>
                             <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 block ml-1">LinkedIn Profile</label>
                             <div className="relative">
                                <FaLinkedin className="absolute top-4 left-4 text-gray-400" />
                                <input
                                    type="url"
                                    name="linkedin"
                                    value={formData.linkedin}
                                    onChange={handleChange}
                                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    placeholder="https://linkedin.com/in/..."
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 block ml-1">Mobile Number</label>
                            <div className="relative">
                                <FaPhone className="absolute top-4 left-4 text-gray-400" />
                                <input
                                    type="tel"
                                    name="mobile"
                                    required
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    placeholder="+91 9876543210"
                                />
                            </div>
                        </div>

                         <div>
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 block ml-1">Key Expertise</label>
                            <div className="relative">
                                <FaGraduationCap className="absolute top-4 left-4 text-gray-400" />
                                <input
                                    type="text"
                                    name="expertise"
                                    required
                                    value={formData.expertise}
                                    onChange={handleChange}
                                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    placeholder="e.g. React, UX Design (comma separated)"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Full Width Bio */}
               <div>
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 block ml-1">Professional Bio</label>
                    <textarea
                        name="bio"
                        rows={5}
                        required
                        value={formData.bio}
                        onChange={handleChange}
                        className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                        placeholder="Tell us about your professional background, achievements, and why you want to be a mentor..."
                    />
                </div>

              <div className="pt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex justify-center py-4 px-6 border border-transparent rounded-xl shadow-lg text-lg font-bold text-white transition-all transform hover:-translate-y-1 ${
                      loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:shadow-xl'
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center">
                         <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing Application...
                    </span>
                  ) : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BecomeMentor;
